import { useEffect, useRef, useState } from "react";
import { api, adminApi } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Send, RefreshCw, Lock, MessageSquare, FileText } from "lucide-react";

const TOKEN_KEY = "superstate_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // Verify on load
  useEffect(() => {
    if (!token) return;
    adminApi()
      .get("/admin/verify")
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Logged in");
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-ink-card border border-ink-line rounded-lg p-8"
        >
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mb-4">
              <Lock size={22} className="text-gold" />
            </div>
            <h1 className="font-heading text-3xl text-white uppercase tracking-wide">
              Admin <span className="text-gold">Access</span>
            </h1>
            <p className="text-white/60 text-sm mt-1">Superstate LLC dashboard</p>
          </div>
          <input
            data-testid="admin-password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-ink-soft border border-ink-line focus:border-gold rounded px-4 py-3 text-white placeholder:text-white/40 focus:outline-none"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loggingIn}
            className="mt-4 w-full bg-gold text-ink font-bold uppercase tracking-widest py-3 rounded hover:bg-gold-hover transition-colors disabled:opacity-50"
          >
            {loggingIn ? "Logging in…" : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  return <Dashboard onLogout={logout} />;
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("quotes");
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-ink-line bg-ink-soft/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="font-heading text-2xl text-gold tracking-wide">
            SUPERSTATE <span className="text-white">ADMIN</span>
          </a>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "quotes", label: "Quote Requests", Icon: FileText },
            { id: "chats", label: "Live Chats", Icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded font-semibold text-sm uppercase tracking-wider transition-all ${
                tab === t.id
                  ? "bg-gold text-ink"
                  : "bg-ink-card text-white/70 border border-ink-line hover:border-gold/50"
              }`}
            >
              <t.Icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "quotes" ? <QuotesPanel /> : <ChatsPanel />}
      </div>
    </div>
  );
}

function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await adminApi().get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-3xl text-white uppercase">Quote Requests</h2>
          <p className="text-white/60 text-sm">{quotes.length} total submissions</p>
        </div>
        <button
          onClick={load}
          data-testid="refresh-quotes"
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold border border-ink-line hover:border-gold px-4 py-2 rounded transition-all"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-white/60">Loading…</div>
      ) : quotes.length === 0 ? (
        <div className="bg-ink-card border border-ink-line rounded-lg p-10 text-center text-white/60">
          No quote requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => (
            <div
              key={q.id}
              data-testid={`quote-${q.id}`}
              className="bg-ink-card border border-ink-line rounded-lg p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-bold text-white text-lg">{q.full_name}</div>
                  {q.company_name && (
                    <div className="text-white/60 text-sm">{q.company_name}</div>
                  )}
                </div>
                <div className="text-xs text-white/50 font-mono">
                  {new Date(q.created_at).toLocaleString()}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-3">
                <Kv k="Phone" v={<a href={`tel:${q.phone}`} className="text-gold hover:underline">{q.phone}</a>} />
                <Kv k="Email" v={<a href={`mailto:${q.email}`} className="text-gold hover:underline break-all">{q.email}</a>} />
                <Kv k="Equipment" v={q.equipment_type} />
                <Kv k="Trucks" v={q.num_trucks} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Kv k="Regions" v={q.regions.join(", ") || "—"} />
                <Kv k="Has dispatcher" v={q.has_dispatcher ? "Yes" : "No"} />
              </div>
              {q.message && (
                <div className="mt-3 pt-3 border-t border-ink-line text-sm text-white/80">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Message</div>
                  {q.message}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">
        {k}
      </div>
      <div className="text-white mt-0.5">{v}</div>
    </div>
  );
}

function ChatsPanel() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await adminApi().get("/admin/chat/sessions");
      setSessions(data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMessages = async (sid) => {
    try {
      const { data } = await api.get(`/chat/messages/${sid}`);
      setMessages(data);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    const t = setInterval(() => loadMessages(active), 5000);
    return () => clearInterval(t);
  }, [active]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !active) return;
    setReply("");
    try {
      await adminApi().post("/admin/chat/reply", { session_id: active, text });
      loadMessages(active);
    } catch (e) {
      toast.error("Failed to send");
    }
  };

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-14rem)]">
      {/* Sessions list */}
      <div className="bg-ink-card border border-ink-line rounded-lg overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-ink-line flex items-center justify-between">
          <div className="font-heading text-lg text-white uppercase tracking-wider">
            Chats
          </div>
          <button onClick={loadSessions} className="text-white/50 hover:text-gold">
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {sessions.length === 0 && (
            <div className="p-6 text-center text-white/50 text-sm">No chats yet</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              data-testid={`chat-session-${s.session_id}`}
              onClick={() => setActive(s.session_id)}
              className={`w-full text-left px-4 py-3 border-b border-ink-line hover:bg-ink-soft transition-colors ${
                active === s.session_id ? "bg-ink-soft border-l-2 border-l-gold" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-bold text-white text-sm truncate">
                  {s.name || "Anonymous"}
                </div>
                {s.needs_human && (
                  <span className="text-[10px] bg-gold text-ink px-1.5 py-0.5 rounded font-bold uppercase">
                    Hot
                  </span>
                )}
              </div>
              <div className="text-white/50 text-xs truncate">
                {s.last_role === "user" ? "👤 " : s.last_role === "admin" ? "★ " : "🤖 "}
                {s.last_message || "No messages"}
              </div>
              {s.phone && (
                <div className="text-gold text-xs mt-1">{s.phone}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div className="bg-ink-card border border-ink-line rounded-lg flex flex-col overflow-hidden">
        {active ? (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${
                    m.role === "admin"
                      ? "justify-end"
                      : m.role === "user"
                      ? "justify-start"
                      : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2 rounded-lg text-sm leading-relaxed ${
                      m.role === "admin"
                        ? "bg-gold text-ink rounded-br-sm"
                        : m.role === "user"
                        ? "bg-ink-card border border-ink-line text-white rounded-bl-sm"
                        : "bg-ink-soft border border-ink-line text-white/80 italic rounded-bl-sm"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1 font-bold">
                      {m.role === "admin" ? "You" : m.role === "bot" ? "Bot" : "Customer"}
                    </div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-ink-line flex gap-2">
              <input
                data-testid="admin-reply-input"
                className="flex-1 bg-ink-soft border border-ink-line rounded px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                placeholder="Type a reply as dispatcher…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendReply()}
              />
              <button
                onClick={sendReply}
                data-testid="admin-reply-send"
                className="bg-gold text-ink w-10 h-10 rounded flex items-center justify-center hover:bg-gold-hover transition-colors"
              >
                <Send size={16} />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/50 text-sm">
            Select a chat to view messages
          </div>
        )}
      </div>
    </div>
  );
}
